import {
  APP_INITIALIZER,
  FactoryProvider,
  inject,
  Injector,
  makeEnvironmentProviders,
  runInInjectionContext
} from '@angular/core';
import { isNotNullOrUndefined, Settings } from '@mri-platform/shared/core';
import { AuthService } from '@mri/angular-wfe-proxy-oidc';
import {
  IPendoSettings,
  NGX_PENDO_SETTINGS_TOKEN,
  NGX_PENDO_WINDOW,
  NgxPendoService,
  pendoInitializer,
  PendoWindow,
  provideNgxPendo
} from 'ngx-pendo';
import { combineLatest } from 'rxjs';

export const PendoSettingsProvider: FactoryProvider = {
  provide: NGX_PENDO_SETTINGS_TOKEN,
  useFactory: (settings: Settings): IPendoSettings => ({
    pendoApiKey: settings.pendo.pendoApiKey,
    pendoInitializerProvider: PendoInitializerProvider
  }),
  deps: [Settings]
};

const initializePendo =
  (
    pendoSettings: IPendoSettings,
    window: PendoWindow,
    authService: AuthService,
    injector: Injector,
    settings: Settings
  ) =>
  async () => {
    await pendoInitializer(pendoSettings, window)();

    runInInjectionContext(injector, () => {
      const pendoSvc = inject(NgxPendoService);
      pendoSvc.initialize({ visitor: { id: 'UNINITIALIZED_USER' }, account: { id: '' } });

      combineLatest({
        user: authService.userLoaded$,
        currentClientId: authService.currentClientId$.pipe(isNotNullOrUndefined())
      }).subscribe(({ user, currentClientId }) => {
        pendoSvc.updateOptions({
          visitor: {
            id: user.sub,
            version: settings.clientVersion
          },
          account: {
            id: currentClientId,
            version: settings.clientVersion
          }
        });
      });
    });
  };

export const PendoInitializerProvider: FactoryProvider = {
  provide: APP_INITIALIZER,
  useFactory: initializePendo,
  deps: [NGX_PENDO_SETTINGS_TOKEN, NGX_PENDO_WINDOW, AuthService, Injector, Settings],
  multi: true
};

export function providePendo() {
  return makeEnvironmentProviders([
    provideNgxPendo({
      pendoApiKey: '', // this value will be overridden by setting provided by PendoSettingsProvider
      pendoInitializerProvider: PendoInitializerProvider
    }),
    PendoSettingsProvider
  ]);
}
